import { createPartnerCheckinQueue } from '../../salon-admin/utils/checkinQueueApi';
import { clearFaceProfileSnapshot, readFaceProfileSnapshot } from '../../face-identity/utils/faceIdentityApi';
import {
  clearActiveCheckinSession,
  clearSalonFaceSession,
  clearVisitHandoff,
  setActiveCheckinSession,
} from './salonFaceSession';
import { handoffFromCheckin } from './visitHandoff';
import { normalizeVisitIntent, VISIT_INTENT_SERVICE } from './visitIntent';

/** Clear prior guest / lobby identity so a new walk-in is not prefilled. */
export function resetWalkInGuestCheckinState() {
  clearSalonFaceSession();
  clearFaceProfileSnapshot();
  clearVisitHandoff();
  clearActiveCheckinSession();
}

function dispatchQueueUpdated() {
  try {
    window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
  } catch {
    /* ignore */
  }
}

/**
 * Add or update lobby check-in when a guest picks visit intent (walk-in kiosk or after face scan).
 */
export async function submitGuestLobbyCheckin({
  visitIntent,
  customerName,
  mobile = '',
  faceUserId = '',
  user = null,
  snapshot = null,
  source = 'salon_guest_kiosk',
} = {}) {
  const explicitFaceId = String(faceUserId || '').trim();
  const useStoredFace = Boolean(explicitFaceId);
  const snap = useStoredFace ? snapshot || readFaceProfileSnapshot() || {} : snapshot || {};
  const profileUser = useStoredFace ? user || snap.user || {} : {};
  const id = explicitFaceId;
  const name = String(
    customerName ||
      (useStoredFace ? profileUser?.name || profileUser?.full_name || snap.user?.name : '') ||
      '',
  ).trim();
  if (!name) {
    throw new Error('Enter your name');
  }

  const intent = normalizeVisitIntent(visitIntent) || VISIT_INTENT_SERVICE;
  const confirmedAt = new Date().toISOString();
  const phone = String(
    mobile ||
      (useStoredFace ? profileUser?.mobile || profileUser?.phone || snap.user?.mobile : '') ||
      '',
  ).trim();

  const checkin = await createPartnerCheckinQueue({
    faceUserId: id || undefined,
    customerName: name,
    mobile: phone,
    selectedServices: [],
    startService: false,
    source,
    customerSnapshot: {
      name,
      full_name: name,
      mobile: phone,
      face_user_id: id || undefined,
      guest_confirmed_at: confirmedAt,
      walk_in: !id,
      organization_id: profileUser?.organization_id || snap.organizationId,
      visit_intent: intent,
      email: profileUser?.email || snap?.user?.email || '',
      gender: profileUser?.gender || snap?.user?.gender || '',
      age: profileUser?.age ?? snap?.user?.age,
      date_of_birth:
        profileUser?.date_of_birth ||
        profileUser?.dob ||
        snap?.user?.date_of_birth ||
        snap?.user?.dob ||
        '',
      profile: snap?.profile || profileUser?.profile || undefined,
    },
  });

  if (!checkin?.id) {
    throw new Error('Could not check in');
  }

  handoffFromCheckin(checkin);
  setActiveCheckinSession({
    id: checkin.id,
    code: checkin.customer_code,
    status: checkin.status,
    faceUserId: id,
  });
  dispatchQueueUpdated();
  return checkin;
}
